import { Component } from '@angular/core';
import {
  ApellidoClienteDto,
  ClienteUnicoDto,
  ClienteUnicoServiceProxy,
  EmailClienteDto,
  NombreCompletoClienteDto,
  TelefonoClienteDto,
} from '../services/service-proxy/service-proxies';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AppService } from '../services/app.service';
import { NombreClienteDto } from '../services/service-proxy/service-proxies';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StoresService, Tienda } from '../services/stores.service';

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss'],
})
export class CustomerFormComponent {

  isLoading = false;
  model?: ClienteUnicoDto;
  idUnico;
  datosNoValidados = false;
  countryCode : string;

  /** Prefijos del prototipo. */
  prefijos = ['+54', '+57', '+598', '+56', '+55', '+34', '+1'];

  telPrefijo = '+54';
  telNumero = '';
  /** El valor tal como vino, para no reformatearlo si el usuario no lo toca. */
  private telefonoOriginal = '';

  // ── Combo de tienda habitual ──
  tiendas : Tienda[] = [];
  tiendaTexto = '';
  tiendaElegida? : Tienda;
  listaAbierta = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private clienteUnicoService: ClienteUnicoServiceProxy,
    private storesService: StoresService,
    private appService: AppService
  ) {
    this.idUnico = appService.idUnico;
    this.countryCode = appService.countryCode!;
    if(this.countryCode === 'CO') this.telPrefijo = '+57';
  }

  ngOnInit() {

    this.isLoading = true;
    this.clienteUnicoService
      .get(this.idUnico, null, false)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        this.model = response;
        this.checkCampos();
        this.separarTelefono();
        this.cargarTiendas();
      });
  }

  get nombreCompleto() : string {
    if(this.model?.nombreCompletoPrincipal) return this.model.nombreCompletoPrincipal;

    return [this.model?.nombrePrincipal, this.model?.apellidoPrincipal]
      .filter(x => x).join(' ');
  }

  // ───────────────────────── teléfono ─────────────────────────

  /**
   * El modelo guarda el telefono como un solo string, y los datos existentes a
   * veces traen el prefijo pegado ("541150541108"). Se separa para poder
   * mostrarlo como en el prototipo, con el pais aparte.
   */
  private separarTelefono() {

    const guardado = (this.model?.telefonoPrincipal ?? '').trim();
    this.telefonoOriginal = guardado;

    const soloDigitos = guardado.replace(/[^\d]/g, '');

    // El prefijo por defecto es el del pais (constructor: +54 AR / +57 CO). Muchos
    // telefonos vienen SIN codigo de pais: en ese caso no hay que dividirlos.
    // Solo se separa si el numero empieza con el codigo del PROPIO pais y queda un
    // numero local plausible (>= 8 digitos). No se prueban otros prefijos (ej. +1):
    // un local que arranca con "1" no es de EEUU, y ese era el bug.
    const codigoPais = this.telPrefijo.replace('+', '');
    if(soloDigitos.startsWith(codigoPais) && (soloDigitos.length - codigoPais.length) >= 8){
      this.telNumero = soloDigitos.substring(codigoPais.length);
    } else {
      this.telNumero = soloDigitos;
    }
  }

  /**
   * Se rearma solo si el usuario lo cambio. Si no lo toco se guarda el valor
   * original tal cual: reformatear un dato que nadie edito es cambiar
   * informacion del cliente sin que la haya pedido.
   */
  private telefonoParaGuardar() : string {

    const nuevo = (this.telNumero ?? '').trim();
    const digitosNuevo = nuevo.replace(/[^\d]/g, '');
    const digitosOriginal = this.telefonoOriginal.replace(/[^\d]/g, '');

    const prefijoOriginalIncluido = digitosOriginal.startsWith(this.telPrefijo.replace('+', ''));

    if(digitosNuevo === digitosOriginal || (prefijoOriginalIncluido && digitosOriginal.endsWith(digitosNuevo)))
      return this.telefonoOriginal;

    return `${this.telPrefijo.replace('+', '')}${digitosNuevo}`;
  }

  // ───────────────────────── tienda habitual ─────────────────────────

  private cargarTiendas() {
    this.storesService.getTiendas().subscribe(tiendas => {
      this.tiendas = tiendas;

      const guardada = this.model?.codigoTienda;

      if(guardada){
        this.tiendaElegida = tiendas.find(t => t.storeCode === guardada);
        if(this.tiendaElegida) this.tiendaTexto = this.tiendaElegida.nombre;
      }
    });
  }

  /**
   * Filtra por nombre, calle y ciudad: el cliente puede no recordar como se
   * llama la sucursal pero si en que calle esta.
   */
  get tiendasFiltradas() : Tienda[] {

    const busqueda = StoresService.normalizar((this.tiendaTexto ?? '').trim());

    if(busqueda.length === 0) return this.tiendas;

    // Cada palabra tiene que aparecer en algun lado, asi "lopez vicente" tambien
    // encuentra "Vicente López".
    const palabras = busqueda.split(/\s+/);

    return this.tiendas.filter(t => palabras.every(p => t.buscable.includes(p)));
  }

  abrirLista() { this.listaAbierta = true; }

  cerrarLista() {
    // Se demora para que alcance a dispararse el click de la opcion.
    setTimeout(() => this.listaAbierta = false, 150);
  }

  elegirTienda(t : Tienda) {
    this.tiendaElegida = t;
    this.tiendaTexto = t.nombre;
    this.listaAbierta = false;
  }

  limpiarTienda() {
    this.tiendaElegida = undefined;
    this.tiendaTexto = '';
  }

  /** Si queda una sola coincidencia al escribir, se autoselecciona. */
  alEscribirTienda() {

    this.listaAbierta = true;
    this.tiendaElegida = undefined;

    const coincidencias = this.tiendasFiltradas;

    if(coincidencias.length === 1 && (this.tiendaTexto ?? '').trim().length >= 3)
      this.tiendaElegida = coincidencias[0];
  }

  descripcion(t : Tienda) : string {
    return [t.calle, t.ciudad].filter(x => x).join(' · ');
  }

  // ───────────────────────── guardar ─────────────────────────

  get puedeGuardar() : boolean {
    // Nombre y DNI no son editables, asi que no se exigen: exigir un campo que
    // el usuario no puede completar es lo que hoy deja el boton muerto.
    return !this.isLoading && (this.telNumero ?? '').trim().length > 0;
  }

  checkCampos() {

    if (!this.model?.nombrePrincipal && this.model?.nombres && this.model.nombres.length > 0) {
      this.model.nombrePrincipal = this.model.nombres[0].nombre;
      this.datosNoValidados = true;
    }

    if (!this.model?.apellidoPrincipal && this.model?.apellidos && this.model.apellidos.length > 0) {
      this.model.apellidoPrincipal = this.model.apellidos[0].apellido;
      this.datosNoValidados = true;
    }

    if (
      !this.model?.nombrePrincipal &&
      !this.model?.apellidoPrincipal &&
      this.model?.nombresCompletos &&
      this.model.nombresCompletos.length > 0
    ) {
      let nombreCompleto = this.model.nombresCompletos[0].nombreCompleto;
      let indiceUltimoEspacio = nombreCompleto!.lastIndexOf(' ');
      this.model.nombrePrincipal = nombreCompleto!.substring(0, indiceUltimoEspacio);
      this.model.apellidoPrincipal = nombreCompleto!.substring(indiceUltimoEspacio + 1);
      this.datosNoValidados = true;
    }

    if (!this.model?.emailPrincipal && this.model?.emails && this.model.emails.length > 0) {
      this.model.emailPrincipal = this.model.emails[0].email;
      this.datosNoValidados = true;
    }

    if (!this.model?.telefonoPrincipal && this.model?.telefonos && this.model.telefonos.length > 0) {
      this.model.telefonoPrincipal = this.model.telefonos[0].telefono;
      this.datosNoValidados = true;
    }
  }

  edit() {

    if(!this.model) return;

    this.model.telefonoPrincipal = this.telefonoParaGuardar();
    this.model.codigoTienda = this.tiendaElegida?.storeCode;

    this.model.nombreCompletoPrincipal = this.nombreCompleto;

    if (!this.model.nombres?.find((x) => x.nombre == this.model?.nombrePrincipal)) {
      this.model.nombres?.push(
        new NombreClienteDto({ nombre: this.model.nombrePrincipal, origen: undefined, status: 1 })
      );
    }

    if (!this.model.apellidos?.find((x) => x.apellido == this.model?.apellidoPrincipal)) {
      this.model.apellidos?.push(
        new ApellidoClienteDto({ apellido: this.model.apellidoPrincipal, origen: undefined, status: 1 })
      );
    }

    if (!this.model.nombresCompletos?.find((x) => x.nombreCompleto == this.model?.nombreCompletoPrincipal)) {
      this.model.nombresCompletos?.push(
        new NombreCompletoClienteDto({ nombreCompleto: this.model.nombreCompletoPrincipal, origen: undefined, status: 1 })
      );
    }

    if (!this.model.telefonos?.find((x) => x.telefono == this.model?.telefonoPrincipal)) {
      this.model.telefonos?.push(
        new TelefonoClienteDto({
          // Antes acá se guardaba nombreCompletoPrincipal: cada cambio de telefono
          // metia el NOMBRE del cliente en la coleccion de telefonos.
          telefono: this.model.telefonoPrincipal,
          status: 1,
          origen: undefined,
          tipo: undefined,
          codigoDeArea: undefined,
        })
      );
    }

    if (!this.model.emails?.find((x) => x.email == this.model?.emailPrincipal)) {
      this.model.emails?.push(
        new EmailClienteDto({ email: this.model.emailPrincipal, origen: undefined, status: 1, tipo: undefined })
      );
    }

    this.model.verificado = true;
    // Colecciones derivadas/de sistema: "Mis datos" no las edita y reenviarlas
    // rompe el update. En particular, mandar fidelizacion con datos hace que el
    // backend devuelva 500 (los clientes con historial de fidelidad no podian
    // guardar). Se omiten del payload (undefined => toJSON no las incluye).
    this.model.mascotas = undefined;
    this.model.fidelizacion = undefined;
    this.model.clustersLog = undefined;
    this.model.clusters = undefined;

    this.isLoading = true;
    this.clienteUnicoService
      .update(this.model)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(() => {
        this.appService.deleteClienteAppData();
        this.snackBar.open('Los cambios se guardaron correctamente.', '', { duration: 2000 });
        this.router.navigateByUrl('/home');
      });
  }
}
